import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.SINO]: {
    sourceUrl: 'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/list.html',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  [CARD_KEYS.SINO_DAWHO]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/DAWHO.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_WBFU]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/WBFU.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_SPORT]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/sportcard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_GREEN]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/cashcard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_DUE_CURRENCY]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/dual-currency-card.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_CASHCARD_JCB]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/cashcardJCB.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_MITSUI]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/co-brand/mitsui-outlet-park-card.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_TAXI]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/co-brand/taxicard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_DAWAY]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/DAWAY.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
};

export const sinoCards: { [key: string]: Card } = {
  [CARD_KEYS.SINO_DAWHO]: {
    name: '永豐大戶現金回饋卡',
    summary: '指定通路 國內7% / 國外8%',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-dawho.png',
    key: 'sino-dawho',
    link: cardLinkMap[CARD_KEYS.SINO_DAWHO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_DAWHO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金',
        max_feedback: '最高回饋 600 現金',
        simplified_condition: '國內基本回饋',
        condition: '此優惠僅「大戶等級」才可享有',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '最高回饋 600 現金',
        simplified_condition: '大戶等級',
        condition: '此優惠僅「大戶等級」才可享有',
        channel: '海外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '8% 現金',
        max_feedback: '最高回饋 900 現金',
        simplified_condition: '大戶等級',
        condition:
          '此優惠僅「大戶等級」才可享有，海外最高8%/國內最高7%（2% 海外基本回饋 + 1% 大戶加碼 + 5% 指定通路加碼）',
        channel: '海外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '4.5%',
    specific_channel_feedback: '5.5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_GREEN]: {
    name: '永豐現金回饋Green卡',
    summary: '精選綠色通路最高5%現金回饋，國外2%、國內1%回饋無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-green.png',
    key: CARD_KEYS.SINO_GREEN,
    link: cardLinkMap[CARD_KEYS.SINO_GREEN].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_GREEN].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition: '國外一般消費享2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '精選綠色通路消費',
        condition:
          '精選國內綠色通路消費最高5%現金回饋（含卡片一般消費基本回饋1%，當期帳單消費，次期帳單回饋），活動期間2025/1/1~2025/6/30',
        channel: '綠色環保消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳享1.2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '種一棵樹',
        max_feedback: '辦卡即享',
        simplified_condition: '辦卡刷永豐',
        condition: '辦卡刷永豐就種一棵樹，永續環保，活動期間2025/1/1~2025/6/30',
        channel: '綠色環保消費',
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '5%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_DUE_CURRENCY]: {
    name: '永豐幣倍卡',
    summary: '雙幣信用卡，國外2%回饋存入外幣帳戶，新戶完成任務最高12%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-due-currency.png',
    key: CARD_KEYS.SINO_DUE_CURRENCY,
    link: cardLinkMap[CARD_KEYS.SINO_DUE_CURRENCY].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_DUE_CURRENCY].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition:
          '國外一般消費享2%現金回饋，無上限，回饋金存入外幣帳戶，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '12%現金回饋',
        max_feedback: '指定通路',
        simplified_condition: '新戶完成任務',
        condition:
          '新戶完成任務最高可享12%回饋（行動支付、海外實體消費、指定網購、旅遊通路），活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳享1.2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '線上換匯最高減6分',
        max_feedback: '無上限',
        simplified_condition: '幣倍卡專屬',
        condition: '幣倍卡專屬線上換匯最高減6分優惠，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '每月8次免ATM跨行手續費',
        max_feedback: '每月8次',
        simplified_condition: '幣倍金融信用卡專屬',
        condition: '幣倍金融信用卡享每月8次免ATM跨行手續費，活動期間2025/1/1~2025/6/30',
        channel: '金融服務',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室/餐廳服務',
        max_feedback: '每年2次',
        simplified_condition: '消費達門檻',
        condition: '龍騰卡機場貴賓室/機場餐廳服務，每年2次，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場週邊停車服務',
        max_feedback: '每年不限次數',
        simplified_condition: '每次最多8天',
        condition: '機場週邊停車服務，每年不限次數，每次最多8天，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition: '支付旅途中全額公共運輸工具費用，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '12%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_CASHCARD_JCB]: {
    name: '永豐現金回饋JCB卡',
    summary: '特選通路最高5%回饋，國外2%、國內1%回饋無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'JCB',
    picture: 'logo_sino-cashcard-jcb.jpg',
    key: CARD_KEYS.SINO_CASHCARD_JCB,
    link: cardLinkMap[CARD_KEYS.SINO_CASHCARD_JCB].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_CASHCARD_JCB].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition: '國外一般消費享2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '特選通路消費',
        condition:
          '特選通路最高5%回饋（含基本回饋，加碼3%回饋每月上限300元），活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '日本消費滿額',
        condition: '日本消費滿額再加碼5%，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳1%回饋無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition: '支付旅途中全額公共運輸工具費用，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年不限次數',
        simplified_condition: '消費達門檻',
        condition: '機場外圍停車服務，每年不限次數，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '5%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_SPORT]: {
    name: '永豐SPORT卡',
    summary: '運動賺回饋，一般消費最高2%豐點，指定支付/通路最高6%豐點',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-sport.png',
    key: CARD_KEYS.SINO_SPORT,
    link: cardLinkMap[CARD_KEYS.SINO_SPORT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_SPORT].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%豐點回饋',
        max_feedback: '無上限',
        simplified_condition: '基本回饋',
        condition:
          '下載註冊汗水不白流APP(勾選卡友)，刷永豐SPORT卡享基本1%豐點回饋，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%豐點回饋',
        max_feedback: '無上限',
        simplified_condition: '運動獎勵',
        condition:
          '運動獎勵加碼1%豐點，需於消費當月於《汗水不白流APP》達10,000大卡或Apple Watch圓滿畫圃10次，活動期間2025/1/1~2025/6/30',
        channel: '運動健身',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6%豐點回饋',
        max_feedback: '指定支付/通路',
        simplified_condition: '指定支付/通路加碼',
        condition:
          '指定支付/通路加碼4%豐點，需於消費當月於《汗水不白流APP》達10,000大卡或Apple Watch圓滿畫圃10次，活動期間2025/1/1~2025/6/30',
        channel: '行動支付',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '行動支付優惠',
        max_feedback: 'Apple Pay/Google Pay',
        simplified_condition: '指定支付通路',
        condition:
          '一般消費綁定Apple Pay／Google Pay／Samsung Pay／Garmin Pay支付享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MOBILE_PAYMENT],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '運動健身優惠',
        max_feedback: 'World Gym等健身中心',
        simplified_condition: '指定健身通路',
        condition:
          'World Gym、健身工廠、TRUE YOGA FITNESS、Curves、台北市運動中心、新北市運動中心等指定健身通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: '運動健身',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '醫藥保健優惠',
        max_feedback: '屏臺氏等藥局',
        simplified_condition: '指定醫藥通路',
        condition:
          '屏臺氏、康是美、寶雅、好心股健檢中心等指定醫藥通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: '醫療保健',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '電競娛樂優惠',
        max_feedback: 'App Store等平台',
        simplified_condition: '指定電競通路',
        condition:
          'App Store、Google Play商店應用程式、Nintendo、PlayStation、Steam等指定電競通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ENTERTAINMENT],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年不限次數',
        simplified_condition: '消費達門檻',
        condition: '機場外圍停車服務，每年不限次數，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%豐點回饋',
    international_feedback: '1%豐點回饋',
    specific_channel_feedback: '6%豐點回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_MITSUI]: {
    name: '永豐MITSUI OUTLET PARK聯名卡',
    summary: '館內消費最高1%豐點回饋，館外0.3%回饋，日本購物最高27%折扣',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-mitsui.png',
    key: CARD_KEYS.SINO_MITSUI,
    link: cardLinkMap[CARD_KEYS.SINO_MITSUI].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_MITSUI].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5-1%豐點回饋',
        max_feedback: '館內消費',
        simplified_condition: '館內消費金額不同',
        condition:
          '館內消費：1-29,999元享0.5%豐點回饋；30,000元享1%豐點回饋，活動期間2025/1/1– 2025/06/30',
        channel: '百貨購物',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.3%豐點回饋',
        max_feedback: '館外消費',
        simplified_condition: '館外一般消費',
        condition: '館外消費享0.3%豐點回饋，活動期間2025/1/1– 2025/06/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '日本購物最高27%折扣',
        max_feedback: '日本地區',
        simplified_condition: '日本地區消費',
        condition: '日本購物最高27%折扣，活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '免費停車及購物娛樂85折起',
        max_feedback: '三井專屬',
        simplified_condition: '三井卡專屬',
        condition: '免費停車及購物娛樂85折起，活動期間2025/1/1– 2025/06/30',
        channel: '百貨購物',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '館外餐飲10%優惠',
        max_feedback: '需登錄',
        simplified_condition: '三井卡專屬',
        condition: '2025年H1三井卡館外餐飲10%優惠（需登錄），活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '和苑三井花園飯店住宿優惠',
        max_feedback: '台北忠孝',
        simplified_condition: '三井卡專屬',
        condition: '和苑三井花園飯店 台北忠孝住宿優惠，活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
    ],
    domestic_feedback: '0.3-1%豐點回饋',
    international_feedback: '0.3%豐點回饋',
    specific_channel_feedback: '1%豐點回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
};
